import { useEffect, useRef, useState, createContext, useCallback } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { DotLottiePlayer } from "@dotlottie/react-player";
import { KamaContextMenu, KamaIcon } from "@kamatech-ir/kama-react-module";
import NotificationAnnouncementJSON from "assets/images/notification-announcement.json";
import styles from "./AuthorizedLayout.module.scss";
import http from "utils/http";
import { useAppDispatch, useAppSelector } from "hooks/redux.hook";
import { clearToken, setIsJustedloggedIn } from "redux/slices/app.slice";
import tools from "utils/tools";
import ProfileSVG from "assets/images/profile.svg";
import BgHomeJPEG from "assets/images/bg-home.jpeg";
import IriSVG from "assets/images/iri.svg";
import packageJson from "../../../package.json";
import classNames from "classnames";

type AuthorizedContextType = {
  clients: any[];
  activeTokens: any[];
  fetchActiveTokens?: any;
};

const isIframe = window.self !== window.top;

export const AuthorizedContext = createContext<AuthorizedContextType>({
  clients: [],
  activeTokens: [],
});

function AuthorizedLayout() {
  const announcementRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isJustLoggedIn, isLoginAnimationVisible } = useAppSelector(
    (state) => state.app
  );

  const [isAnnouncementVisible, setIsAnnouncementVisible] =
    useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<any>({});
  const [clients, setClients] = useState<any[]>([]);
  const [activeTokens, setActiveTokens] = useState<any[]>([]);

  const fetchAuthenticationTest = async () => {
    try {
      const response = await http.post({
        url: "/api/Authentication/Test",
      });

      console.log(response.data);
    } catch (error) {
      console.log(error);
    }
  };
  const logout = async () => {
    try {
      await http.post({
        url: "/api/Authentication/Logout",
        data: {
          token: localStorage.token,
          undoOnlyApp: false,
        },
      });
      dispatch(clearToken());
    } catch (error) {
      tools.alert.error(
        "خطا در خروج از سامانه، لطفا لحظاتی دیگر مجددا تلاش کنید."
      );
    }
  };
  const fetchActiveTokens = useCallback(async () => {
    try {
      const activeTokenResponse = await http.post({
        url: "/api/IssuedToken/ListActiveToken",
      });
      setActiveTokens(activeTokenResponse.data);
    } catch (error) {}
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // fetch current user
        const currentUserResponse = await http.get({
          url: "/api/Sso/GetCurrentUser",
        });
        setCurrentUser(currentUserResponse.data);

        // display welcome message right after login
        if (!isIframe && isJustLoggedIn) {
          dispatch(setIsJustedloggedIn(false));
          tools.alert.success(
            `${currentUserResponse.data.name} عزیز، به سکوی یکپارچه نظام اداری خوش آمدید.`
          );
        }

        // fetch clients
        const clientsResponse = await http.post({
          url: "/api/Sso/ListClientsForUser",
        });
        setClients(clientsResponse.data);

        // fetch profile picture
        const profilePictureResponse = await http.post({
          url: "/api/Authentication/GetProfilePicture",
        });
        setCurrentUser({
          ...currentUserResponse.data,
          profilePicture: profilePictureResponse.data
            ? profilePictureResponse.data?.base64
            : "",
        });

        // fetch active tokens
        await fetchActiveTokens();
      } catch (error: any) {
        tools.alert.error(error?.messages);
      }
    };

    fetchInitialData();
  }, [dispatch, fetchActiveTokens, isJustLoggedIn]);

  return (
    <AuthorizedContext.Provider
      value={{
        clients,
        activeTokens,
        fetchActiveTokens,
      }}
    >
      <div
        className={classNames(styles.wrapper, {
          [styles.isVisible]: !isLoginAnimationVisible,
        })}
      >
        {isAnnouncementVisible ? (
          <div
            className={styles.announcementWrapper}
            onClick={(event) => {
              if (event.target === announcementRef.current) {
                setIsAnnouncementVisible(false);
              }
            }}
            ref={announcementRef}
          >
            <div className={styles.announcementModal}>
              <div className={styles.announcementModalContent}>
                <KamaIcon
                  icon="xmark"
                  onClick={() => {
                    setIsAnnouncementVisible(false);
                  }}
                />
                <div className={styles.content}>
                  <h4>در حال توسعه‌ی قابلیتی جدید هستیم!</h4>
                  <div className={styles.animation}>
                    <DotLottiePlayer
                      src={NotificationAnnouncementJSON}
                      autoplay
                      loop
                    />
                  </div>
                  <p>
                    به‌زودی از طریق این بخش می‌توانید تمامی اعلان‌های سیستمی
                    زیرسامانه‌هایی را که به آن دسترسی دارید، مشاهده نمایید.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className={styles.container}>
          <header>
            <div className={styles.appName}>
              <h1>سیــنا</h1>
              <h2>سکوی یکپارچه نظام اداری</h2>
            </div>
            <div className={styles.userInfo}>
              {currentUser.username === "0073929190" ? (
                <KamaIcon
                  icon="monitor-list"
                  className={styles.announcement}
                  onClick={fetchAuthenticationTest}
                />
              ) : null}
              <KamaIcon
                icon="bell"
                className={styles.announcement}
                title="اعلان‌ها"
                onClick={() => {
                  setIsAnnouncementVisible(true);
                }}
              />
              <KamaContextMenu
                className={styles.dropdown}
                label={currentUser.name ?? "..."}
                icon="chevron-down"
                items={(["0073929190", "0016866622", "2282863178"].indexOf(
                  currentUser.username
                ) !== -1
                  ? [
                      {
                        label: "پروفایل",
                        onClick: () => {
                          navigate("/profile");
                        },
                      },
                    ]
                  : []
                ).concat([{ label: "خروج", onClick: logout }])}
                down
              />
              <div className={styles.profilePictureContainer}>
                <div
                  className={styles.profilePicture}
                  style={{
                    backgroundImage: `url(${
                      currentUser.profilePicture
                        ? `data:image/png;base64,${currentUser.profilePicture}`
                        : ProfileSVG
                    })`,
                  }}
                />
                <div
                  className={styles.profilePictureShadow}
                  style={{
                    backgroundImage: `url(${
                      currentUser.profilePicture
                        ? `data:image/png;base64,${currentUser.profilePicture}`
                        : ProfileSVG
                    })`,
                  }}
                />
              </div>
            </div>
          </header>

          <div className={styles.introduction}>
            <img src={BgHomeJPEG} className={styles.image} alt="سینا" />
            <div className={styles.description}>
              <div className={styles.heading}>
                <img src={IriSVG} alt="ریاست جمهوری" />
                <div className={styles.titles}>
                  <h3>ریاست جمهوری</h3>
                  <h4>سازمان اداری و استخدامی کشور</h4>
                </div>
              </div>
              <summary>
                سازمان اداری و استخدامی کشور بعنوان یک سازمان دولتی در ایران،
                وظایف رئیس‌جمهور در حوزه اداری و استخدامی را برعهده دارد و رئیس
                آن معاون رئیس‌جمهور است. طبق اصل ۱۲۶ قانون اساسی، رئیس‌جمهور
                مسئولیت امور برنامه و بودجه و امور اداری و استخدامی کشور را
                مستقیما برعهده دارد و می‌تواند اداره آنها را به عهده دیگری
                بگذارد. در قانون‌اساسی جمهوری اسلامی ایران مطابق با اصل ۳ بند
                ۱۰، ایجاد نظام اداری صحیح و حذف تشکیلات غیر ضرور، جز فعالیت‌های
                این سازمان است.
              </summary>
            </div>
          </div>

          <Outlet />

          <footer>
            <p>
              تمامی حقوق این پلتفرم متعلق به سازمان اداری و استخدامی کشور
              می‌باشد. نسخه <span>{packageJson.version}</span>
            </p>
            <p>Single Sign On Technology</p>
          </footer>
        </div>
      </div>
    </AuthorizedContext.Provider>
  );
}

export default AuthorizedLayout;
